import { useEffect, useState } from "react";
import {
  BackButton,
  Box,
  NotAuthorizedForResource,
  SpanStyled,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  AttachmentIcon,
  Download,
  HalfEyeIcon,
} from "../../../../components/styled/AllImages";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../../components/admin/Breadcrumbs/Breadcrumbs";
import ViewPage from "../../../../components/common/pageView/pageView";
import { viewarchive } from "../../../../helpers/dummyConstants";
import CabinetsApi from "../../../../services/api/cabinetsApi";
import HistoryApi from "../../../../services/api/historyApi";
import { toast } from "react-toastify";
import { getModuleOperations, hasPlanFeature } from "../../../../utils";
import FileViewerModal from "../../../../components/common/fileViewer/FileViewer";
import { ArchiveWrapper, FormDataContainer } from "./styled";
import {
  handleFileDownload,
  handleFilePreview,
  revokeBlobUrl,
} from "../../../../helpers/fileDownloadHelper"; // ✅ Same helpers as ArchiveDetail

const TemporaryArchiveDetail = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { id, fileId, archiveId } = useParams();
  const [accessDenied, setAccessDenied] = useState(false);
  const [initArchiveId, setInitArchiveId] = useState(null); // ✅ Fixed: was {}
  const [archiveDetail, setArchiveDetail] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const createArchiveHistory = async (data) => {
    try {
      await HistoryApi.createHistory(data);
    } catch (error) {
      console.error("History creation error:", error);
    }
  };

  // ✅ Uses the same shared helper as ArchiveDetail (fetches as blob → local URL)
  const handleDownload = async (filePath) => {
    await handleFileDownload(
      filePath,
      setDownloading,
      {
        starting_download: t("starting_download"),
        downloading: t("downloading"),
        remaining_time: t("remaining_time"),
        download_complete: t("download_complete"),
        download_failed_try_again: t("download_failed_try_again"),
      },
      initArchiveId
    );
  };

  // ✅ Uses the same shared helper as ArchiveDetail (fetches as blob → local URL)
  const handlePreview = async (filePath) => {
    await handleFilePreview(
      filePath,
      setViewingFile,
      setShowModal,
      setDownloading,
      {
        preview_failed_try_again: t("preview_failed_try_again"),
      },
      initArchiveId
    );
  };

  const renderPreview = (path) => {
    const isDownload = Number(archiveDetail?.access_type) === 2;

    if (!path) {
      return <span>{path}</span>;
    }

    const decodedPath = decodeURIComponent(path);
    const fileName = decodedPath.split("/").pop();

    return (
      <Box>
        <Box className="d-flex gap-2 align-items-center">
          <Box>
            <AttachmentIcon width={50} height={70} />
            <SpanStyled>{fileName}</SpanStyled>
          </Box>
          {downloading ? (
            <ThemeLoader />
          ) : (
            <Box className="d-flex align-items-center justify-content-center gap-2">
              {/* View/Preview Button */}
              <Box className="p-2 rounded" bg="var(--bgSecondary)">
                <HalfEyeIcon
                  className="cursor-pointer"
                  onClick={() => handlePreview(path)}
                  title={t("view") || "View"}
                  style={{ cursor: "pointer" }}
                />
              </Box>

              {/* Download Button */}
              {getModuleOperations(3, 6) && isDownload && (
                <Box className="p-2 rounded" bg="var(--bgSecondary)">
                  <Download
                    className="cursor-pointer"
                    onClick={() => handleDownload(path)}
                    title={t("download") || "Download"}
                    style={{ cursor: "pointer" }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const getSingleArchive = async () => {
    try {
      setAccessDenied(false);
      setLoading(true);
      const { code, data } = await CabinetsApi.openSingleArchive(
        id,
        fileId,
        archiveId
      );

      if (code === 200) {
        setArchiveDetail({
          ...data,
          archive_type_id:
            data?.archive_type_id == 1
              ? t("Document")
              : data?.archive_type_id == 2
              ? t("Video")
              : data?.archive_type_id == 3
              ? t("Voice")
              : data?.archive_type_id == 4
              ? t("Picture")
              : t("Other"),
          archive_status_id:
            data?.archive_status_id == 1
              ? t("sent")
              : data?.archive_status_id == 2
              ? t("received")
              : t("created"),
        });
        setInitArchiveId(data?.id); // ✅ Correctly set for history tracking

        if (hasPlanFeature(1)) {
          createArchiveHistory({
            archive_id: data?.id,
            action_type: "view",
            accessed_at: new Date().toLocaleString(),
          });
        }
      } else if (code === 403) {
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Get archive error:", error);
      toast.error(t("error_loading_archive") || "Error loading archive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSingleArchive();
  }, [language]);

  // ✅ Properly revokes blob URL on modal close (prevents memory leaks)
  const handleCloseModal = () => {
    setShowModal(false);
    revokeBlobUrl(viewingFile?.url);
  };

  return (
    <ArchiveWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("temporary_archive_details")}
            onClick={() =>
              navigate(
                `/temporary-cabinets/${id}/temporary-files/${fileId}/temporary-archives`
              )
            }
          />
          <Breadcrumbs />
        </Box>
      </Box>

      {accessDenied ? (
        <NotAuthorizedForResource />
      ) : (
        <Box className="content-container">
          <Row className="mt-3">
            <Col md={12}>
              <FormDataContainer className="dataContainer">
                {loading ? (
                  <ThemeLoader />
                ) : (
                  <>
                    {viewarchive.map((view) => {
                      if (
                        view.id === 2 &&
                        archiveDetail.archive_type_id != t("Document")
                      ) {
                        return null;
                      }
                      if (
                        view.id === 3 &&
                        archiveDetail.archive_type_id != t("Video") &&
                        archiveDetail.archive_type_id != t("Audio")
                      ) {
                        return null;
                      }
                      if (
                        view.id === 4 &&
                        archiveDetail.archive_type_id != t("Other")
                      ) {
                        return null;
                      }

                      return (
                        <ViewPage
                          key={view.id}
                          label={view.label}
                          text={archiveDetail[view.name]}
                          render={
                            view.name === "attachment"
                              ? () => renderPreview(archiveDetail[view.name])
                              : null
                          }
                        />
                      );
                    })}
                  </>
                )}
              </FormDataContainer>
            </Col>
          </Row>
        </Box>
      )}

      {/* ✅ onHide now properly revokes blob URL */}
      <FileViewerModal
        show={showModal}
        onHide={handleCloseModal}
        fileUrl={viewingFile?.url}
        fileType={viewingFile?.type}
      />
    </ArchiveWrapper>
  );
};

export default TemporaryArchiveDetail;